import { FieldValue } from "backend/datainput";
import { ICardElementWithChildren } from "backend/model/model";
import * as types from "client-api-workflow/types";
import ValueInputForm from "design/components/forms/ValueInputForm";
import InitiativeHeading from "design/components/texts/InitiativeHeading";
import { t } from "i18next";
import React, { useState } from "react";
import ActionDialog from "./ActionDialog";
import useAllActions, { type ActionData } from "backend/hooks/actions/useAllActions";
import ActionsPreview from "design/components/cards/ActionsPreview";

const KPIInputForm = (props: {
	kpi: ICardElementWithChildren;
	childElements: ICardElementWithChildren[];
	comments?: types.WFComment[];
	attachments?: types.FileValue[];
	onSubmit?: (values: FieldValue[]) => void;
	isReadOnly?: boolean;
}) => {
	const { kpi, childElements, comments, attachments } = props;
	const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);

	const { data: allActions } = useAllActions();
	const actions: ActionData[] | undefined = React.useMemo(
		() =>
			allActions?.filter(
				(action) => action.searchKpiInitiativeId === (kpi?.id || "")
			),
		[allActions, kpi?.id]
	);

	const isNumeric = (s: string) => !isNaN(+s) && isFinite(+s) && !/e/i.test(s);

	// Get KPI info for the action dialog
	const kpiInfo = {
		id: kpi?.id || "",
		typename: "KPI" as const,
		name: kpi?.name || "",
	};

	return (
		<>
			<ValueInputForm
				buttons={[
					{
						tag: "submit",
						style: "submit",
						text: t("workflowinput.form.Submit").toString(),
						onClick: props.onSubmit,
					},
				]}
				key={kpi?.workflowInstance?.wfInstanceId}
				element={kpi!}
				childElements={childElements}
				onIntermediateChange={(values) => {}}
				fields={[
					{
						title: t("workflowinput.form.ACT").toString(),
						series: "ACT",
						initialValue: props.kpi.ACT_valuewithoutsymbol,
						element: kpi,
						inputType: "string",
						isDisabled: props.isReadOnly,
						unitSeries: "ACT_unitname",
						validate: (state) => {
							// allow empty ACT values for initiative
							if (
								typeof state.field.value === "undefined" ||
								state.field.value === ""
							) {
								return {
									success: true,
									errorMessages: [],
								};
							}

							if (!state.field.value) {
								return {
									success: false,
									errorMessages: [
										t("workflowinput.form.errors.Value can not be empty"),
									],
								};
							}

							if (!isNumeric(state.field.value)) {
								return {
									success: false,
									errorMessages: [
										t("workflowinput.form.errors.Value must be number"),
									],
								};
							}

							return {
								success: true,
								errorMessages: [],
							};
						},
					},
					{
						title: t("workflowinput.form.Attachments"),
						element: kpi,
						inputType: "attachments",
						initialFiles: attachments,
						isDisabled: props.isReadOnly,
					},
					{
						title: t("Justifications & Recommendations"),
						element: kpi,
						inputType: "comment",
						comments: comments,
						isDisabled: props.isReadOnly,
						initialValue: comments && comments[0] ? comments[0].description : "",
					},
				]}
			/>
			{/* Add Action Button - appears after Actions and Recommendations */}
			{!props.isReadOnly && (
				<>
					<div style={{ padding: "20px 20px 0 20px" }}>
						<InitiativeHeading>{t("Actions.Actions")}</InitiativeHeading>
					</div>
					<div style={{ padding: "0 20px 20px 20px" }}>
						<button
							type="button"
							onClick={() => setIsActionDialogOpen(true)}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "8px",
								padding: "10px 16px",
								backgroundColor: "#1976d2",
								color: "#fff",
								border: "none",
								borderRadius: "4px",
								cursor: "pointer",
								fontSize: "14px",
								fontWeight: 500,
							}}
						>
							<span>+</span>
							{t("Add Action")}
						</button>
					</div>
				</>
			)}

			{/* Action Dialog */}
			<ActionDialog
				open={isActionDialogOpen}
				onClose={() => setIsActionDialogOpen(false)}
				kpiOrInitiative={kpiInfo}
			/>
			{/* Actions Preview */}
			<div style={{ padding: "0 20px 20px" }}>
				<ActionsPreview
					actions={actions || []}
					kpiInitiativeName={kpiInfo.name}
				/>
			</div>
		</>
	);
};

export default KPIInputForm;

